import { useEffect, useRef, useState, useCallback } from 'react';

// Custom hook for scroll-triggered animations (Optimized)
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  const { threshold = 0.05, rootMargin = '50px 0px', triggerOnce = true } = options;

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
          if (triggerOnce) {
            observer.unobserve(currentRef);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

// Animated counter component
export const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.5 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(easeOutQuart * end);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toFixed(decimals)}{suffix}
    </span>
  );
};

// Scroll-triggered wrapper component
export const ScrollReveal = ({ 
  children, 
  animation = 'fade-in-up', 
  delay = 0, 
  duration = 800,
  className = '' 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const animationClasses = {
    'fade-in-up': 'scroll-fade-in-up',
    'fade-in-left': 'scroll-fade-in-left',
    'fade-in-right': 'scroll-fade-in-right',
    'scale-in': 'scroll-scale-in',
    'blur-in': 'scroll-blur-in',
  };

  return (
    <div 
      ref={ref}
      className={`${animationClasses[animation] || 'scroll-fade-in-up'} ${isVisible ? 'visible' : ''} ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        transitionDuration: `${duration}ms`
      }}
    >
      {children}
    </div>
  );
};

// Staggered children animation wrapper
export const StaggerReveal = ({ children, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div 
      ref={ref}
      className={`stagger-children ${isVisible ? 'visible' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

export default useScrollAnimation;
